import React, { useMemo } from 'react';
import { useIsMobile } from '@hooks/responsive/useIsMobile';
import styled from '@emotion/styled';

// colors
import { primary, blue, gray, red, white, black } from '@styles/Colors';

// component
import TextInput from '@components/_atoms/TextInput';
import Dropdown from '@components/_atoms/Dropdown';
import CheckBox from '@components/_molecules/CheckBox';
import Typography from '@components/_atoms/Typography';
import Button from '@components/_atoms/Button';

// constants
import {
  DropdownSize,
  TextInputAlign,
  TextInputVariant,
  TypoVariant,
  ButtonColor,
  ButtonSize,
  ButtonType,
} from '@constants/atoms';
import ButtonCode from '@constants/log/buttonCode';

type Props = {
  title?: string;
  subTitle?: string;
  textInput: TextInputType;
  checkBox?: CheckBoxType | null;
  dropdown?: DropdownType;
  button?: ButtonPropsType;
  errorMessage?: string;
  successMessage?: string;
  isRequired?: boolean;
};

type TextInputType = {
  variant?: TextInputVariant;
  placeholder?: string;
  name?: string;
  value?: string | number;
  type?: string;
  align?: TextInputAlign;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onEnter?: () => void;
  onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  onKeyDown?: (e: React.KeyboardEvent) => void;
  autoComplete?: string;
};

type CheckBoxType = {
  title?: string;
  checkStatus?: boolean;
  onClick?: () => void;
};

type DropdownType = {
  name?: string;
  placeholder?: string;
  select?: string;
  option?: string[];
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
};

type ButtonPropsType = {
  text: string;
  onClick?: () => void;
  color?: ButtonColor;
  size?: ButtonSize;
  type?: ButtonType;
  disabled?: boolean;
  isLoading?: boolean;
  steelbosoId?: string;
};

const TextInputBoxWrapper = styled.div`
  display: grid;
  row-gap: 8px;
  flex-grow: 1;
`;

const InputButtonWrapper = styled.div`
  display: flex;
  gap: 8px;
  width: 100%;
`;

const HighLightRed = styled.span`
  color: ${red.red3};
`;

export default function TextInputBox({
  title,
  subTitle,
  textInput,
  checkBox,
  dropdown,
  button,
  errorMessage,
  successMessage,
  isRequired = false,
}: Props) {
  const isMobile = useIsMobile();

  const textInputVariant = useMemo(() => {
    if (!checkBox) return textInput?.variant;

    if (checkBox.checkStatus) {
      return TextInputVariant.FIXED;
    } else {
      return TextInputVariant.DEFAULT;
    }
  }, [textInput, checkBox]);

  const handleKeyDownTextInput = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      textInput?.onEnter?.();
    }
  };

  return (
    <TextInputBoxWrapper>
      <>
        {title && (
          <Typography variant={isMobile ? TypoVariant.SH4 : TypoVariant.SH3} color={primary.gray}>
            {title}
            {isRequired && <HighLightRed> *</HighLightRed>}
          </Typography>
        )}
      </>
      <>
        {subTitle && (
          <Typography variant={isMobile ? TypoVariant.B5 : TypoVariant.B4} color={primary.gray}>
            {subTitle}
          </Typography>
        )}
      </>
      <>
        {checkBox && (
          <CheckBox title={checkBox?.title} checkStatus={checkBox?.checkStatus} onClick={checkBox?.onClick} />
        )}
      </>
      <>
        {dropdown && (
          <Dropdown
            name={dropdown?.name}
            select={dropdown?.select}
            option={dropdown?.option}
            onClick={dropdown?.onClick}
            size={DropdownSize.SMALL}
          />
        )}
      </>
      <>
        {button ? (
          <InputButtonWrapper>
            <TextInput
              variant={textInputVariant}
              placeholder={textInput?.placeholder}
              name={textInput?.name}
              value={textInput?.value}
              type={textInput?.type}
              align={textInput?.align}
              onChange={textInput?.onChange}
              onKeyDown={textInput?.onKeyDown ?? handleKeyDownTextInput}
              onFocus={textInput?.onFocus}
              onBlur={textInput?.onBlur}
              autoComplete={textInput.autoComplete}
              style={{ width: '100%' }}
            />
            <Button
              text={button.text}
              onClick={button.onClick}
              color={button.color ?? ButtonColor.ContainedBlue}
              size={button.size ?? ButtonSize.Large}
              type={button.type ?? ButtonType.Button}
              disabled={button.disabled}
              isLoading={button.isLoading}
              steelbosoId={button.steelbosoId ?? ButtonCode.BTN_MODAL_CONFIRM}
            />
          </InputButtonWrapper>
        ) : (
          <TextInput
            variant={textInputVariant}
            placeholder={textInput?.placeholder}
            name={textInput?.name}
            value={textInput?.value}
            type={textInput?.type}
            align={textInput?.align}
            onChange={textInput?.onChange}
            onKeyDown={textInput?.onKeyDown ?? handleKeyDownTextInput}
            onFocus={textInput?.onFocus}
            onBlur={textInput?.onBlur}
            autoComplete={textInput.autoComplete}
          />
        )}
      </>
      <>
        {errorMessage && (
          <Typography variant={isMobile ? TypoVariant.B5 : TypoVariant.B4} color={red.red3}>
            {errorMessage}
          </Typography>
        )}
        {successMessage && (
          <Typography variant={isMobile ? TypoVariant.B5 : TypoVariant.B4} color={primary.blue}>
            {successMessage}
          </Typography>
        )}
      </>
    </TextInputBoxWrapper>
  );
}
